import React from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { makeStyles } from "@material-ui/core";

const useStyles = makeStyles(() => ({
  root: {
    position: "fixed",
    bottom: 76,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 1050,
    display: "inline-flex",
    alignItems: "center",
    maxWidth: "min(560px, calc(100vw - 24px))",
    padding: "5px 12px",
    borderRadius: 8,
    background: "rgba(24, 28, 30, 0.78)",
    backdropFilter: "blur(8px)",
    color: "rgba(255, 255, 255, 0.92)",
    fontSize: 12,
    lineHeight: 1.35,
    fontWeight: 500,
    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.16)",
    pointerEvents: "none",
  },
  rootPanelOpen: {
    maxWidth: "min(480px, calc(50vw - 24px))",
    left: "25%",
  },
  rootToolAreaOpen: {
    maxWidth: "min(560px, calc(75vw - 24px))",
    left: "37.5%",
  },
  miniRoot: {
    position: "absolute",
    top: 6,
    left: 6,
    right: 34,
    zIndex: 1100,
    padding: "3px 8px",
    borderRadius: 6,
    background: "rgba(24, 28, 30, 0.88)",
    backdropFilter: "blur(6px)",
    color: "rgba(255, 255, 255, 0.92)",
    fontSize: 10,
    lineHeight: 1.2,
    fontWeight: 500,
    boxShadow: "0 1px 6px rgba(0, 0, 0, 0.18)",
    pointerEvents: "none",
  },
  text: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));

const ActiveSessionBanner = () => {
  const classes = useStyles();
  const intl = useIntl();
  const meId = useSelector((state) => state.me.id);
  const isMini = useSelector((state) => state.call.displayMini);
  const whiteBoardOpen = useSelector(
    (state) => state.whiteboard.whiteBoardOpen
  );
  const toolAreaOpen = useSelector((state) => state.toolarea.toolAreaOpen);
  const session = useSelector((state) => state.remoteControl);
  const host = useSelector((state) =>
    session && session.hostPeerId ? state.peers[session.hostPeerId] : null
  );
  const controller = useSelector((state) =>
    session && session.controllerPeerId
      ? state.peers[session.controllerPeerId]
      : null
  );

  if (!session || session.status !== "active") return null;

  const isHost = session.hostPeerId === meId;
  const isController = session.controllerPeerId === meId;
  const controllerName =
    (controller && controller.displayName) ||
    intl.formatMessage({
      id: "label.participant",
      defaultMessage: "Participant",
    });
  const hostName =
    (host && host.displayName) ||
    intl.formatMessage({
      id: "label.participant",
      defaultMessage: "Participant",
    });

  let bannerId = "remoteControl.activeBannerOther";
  let bannerValues = { controller: controllerName, host: hostName };

  if (isController) {
    bannerId = "remoteControl.activeBannerController";
    bannerValues = { host: hostName };
  } else if (isHost) {
    bannerId = "remoteControl.activeBannerHost";
    bannerValues = { controller: controllerName };
  }

  const rootClassName = isMini
    ? classes.miniRoot
    : [
        classes.root,
        whiteBoardOpen ? classes.rootPanelOpen : null,
        !whiteBoardOpen && toolAreaOpen ? classes.rootToolAreaOpen : null,
      ]
        .filter(Boolean)
        .join(" ");

  const banner = (
    <div className={rootClassName}>
      <span className={classes.text}>
        <FormattedMessage
          id={bannerId}
          defaultMessage="{controller} is controlling {host}'s screen"
          values={bannerValues}
        />
      </span>
    </div>
  );

  if (isMini) return banner;

  return ReactDOM.createPortal(banner, document.body);
};

export default ActiveSessionBanner;
